import React, { useCallback, useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { AutocompleteGroupSearch, AutocompleteSearch, Loader, WarningText } from 'components/UIElements'
import { DEPLOYMENT_KEYS } from 'utils/constants'
import {
  armsToCheckedObject,
  armsToTags,
  getChainDeploymentStudyScheduleIds,
  getPersistedStudyScheduleIds,
} from 'utils/chainDeploymentUtils'
import { alignExpiryWithDeployment, getArmsMissingExpireVisits } from 'utils/chainExpiryUtils'
import { setObjectProperty } from 'utils/object'
import STRINGS from 'utils/strings'

const CHAIN_DEPLOY_BY_SS_VISIT = DEPLOYMENT_KEYS.chainDeployByVisit
const ARM_SEARCH_PLACEHOLDER = 'Search for an arm...'
const DEPLOY_VISIT_ERROR_KEY = 'chain_deployment_info.visit_ids'

const getVisitDay = visit => visit?.visit_day ?? visit?.day ?? 0

const sortVisitsByDay = visits =>
  [...(visits || [])].sort((a, b) => getVisitDay(a) - getVisitDay(b))

const isNonCycledCycle = cycle => {
  if (cycle?.is_dummy || cycle?.isDummy || cycle?.metadata?.is_dummy) return true
  const visits = cycle?.visits || []
  return visits.length === 1 && !!cycle?.name && visits[0]?.name === cycle.name
}

const getScheduleAndVisitName = ({ visit_id, visit_schedule_id, visitTemplates }) => {
  const currentVisitSchedule = visitTemplates.reduce((_, currentTemplate) => {
    if (currentTemplate.id === visit_schedule_id) return currentTemplate
    return _
  }, {})
  const _visitScheduleName = currentVisitSchedule.name
  const currentVisit = currentVisitSchedule?.visits?.reduce((_, nextVisit) => {
    if (nextVisit.id === visit_id) return nextVisit
    return _
  }, {})

  return { _visitScheduleName, _visitName: currentVisit?.name }
}

const resolveVisitScheduleId = ({ visit_id, visit_schedule_id, visitTemplates, cycles }) => {
  if (visit_schedule_id) return visit_schedule_id

  const templateMatch = visitTemplates.find(template =>
    template.visits?.some(visit => visit.id === visit_id),
  )
  if (templateMatch) return templateMatch.id

  const cycleMatch = cycles?.find(cycle => cycle.visits?.some(visit => visit.id === visit_id))
  return cycleMatch?.id || ''
}

const InstrumentVisitSelection = props => {
  const {
    chain_deployment_info,
    chain_expiry_info = {},
    clearPrevalidatedError,
    disabled,
    errors,
    fetchVisitTemplates,
    studyID,
    updateChainDeployInfo,
    updatePrevalidatedErrors,
    visitTemplates = [],
    isVisitCycleSupported,
    isArmsSupported,
    studyVisitSchedule = [],
    updateScheduleKey,
    isReadOnlyState,
  } = props

  const isSelectionDisabled = disabled || isReadOnlyState

  const getAvailableArms = useCallback(() => {
    return studyVisitSchedule?.map(item => ({
      key: item?.id,
      text: item?.name,
    }))
  }, [studyVisitSchedule])

  const availableArms = useMemo(() => {
    return getAvailableArms()
  }, [getAvailableArms])

  const [visitTemplatesLoading, setVisitTemplatesLoading] = useState(false)
  const [checkedVisitTemplate, setCheckedVisitTemplate] = useState({})
  const [checkedArms, setCheckedArms] = useState([])

  const {
    visit_id,
    visit_schedule_id,
    study_schedule_id,
    visit_ids: storedVisitIds = [],
    visit_schedule_ids: storedVisitScheduleIds = [],
  } = chain_deployment_info || {}
  const selectedArmIds = useMemo(
    () => getChainDeploymentStudyScheduleIds(chain_deployment_info),
    [chain_deployment_info],
  )

  const getCyclesList = useCallback(() => {
    if (!isVisitCycleSupported) return []

    const armIds = checkedArms.length > 0 ? checkedArms.map(arm => arm.key) : selectedArmIds
    return armIds.reduce((acc, armId) => {
      const arm = studyVisitSchedule?.find(item => item.id === armId)
      const armCycles = arm?.phases?.reduce((phaseAcc, phase) => [...phaseAcc, ...(phase?.cycles || [])], []) || []
      return [
        ...acc,
        ...armCycles.map(cycle => ({
          ...cycle,
          armId: arm?.id,
          armName: arm?.name,
          isDummy: isNonCycledCycle(cycle),
        })),
      ]
    }, [])
  }, [checkedArms, isVisitCycleSupported, selectedArmIds, studyVisitSchedule])

  const cycles = getCyclesList()

  const getArmIdForVisitScheduleId = useCallback(
    visitScheduleId => {
      if (!visitScheduleId) return null

      for (const arm of studyVisitSchedule || []) {
        for (const phase of arm.phases || []) {
          if ((phase.cycles || []).some(cycle => cycle.id === visitScheduleId)) {
            return arm.id
          }
          if ((phase.visit_templates || []).some(template => template.id === visitScheduleId)) {
            return arm.id
          }
        }
      }

      return null
    },
    [studyVisitSchedule],
  )

  const getStoredVisitScheduleId = useCallback(
    (visitId, index) => {
      if (Array.isArray(storedVisitScheduleIds) && storedVisitScheduleIds[index]) {
        return storedVisitScheduleIds[index]
      }
      // Older multi-visit payloads stored unique schedule IDs only
      if (Array.isArray(storedVisitScheduleIds) && storedVisitScheduleIds.length === 1) {
        return storedVisitScheduleIds[0]
      }
      if (visit_id === visitId && visit_schedule_id) {
        return visit_schedule_id
      }
      return visit_schedule_id || ''
    },
    [storedVisitScheduleIds, visit_id, visit_schedule_id],
  )

  const deployVisits = useMemo(() => {
    if (Array.isArray(storedVisitIds) && storedVisitIds.length > 0) {
      return storedVisitIds
        .map((visitId, index) => {
          const resolvedVisitScheduleId = resolveVisitScheduleId({
            visit_id: visitId,
            visit_schedule_id: getStoredVisitScheduleId(visitId, index),
            visitTemplates,
            cycles,
          })
          return {
            visit_id: visitId,
            visit_schedule_id: resolvedVisitScheduleId,
            study_schedule_id:
              getArmIdForVisitScheduleId(resolvedVisitScheduleId) ||
              study_schedule_id ||
              selectedArmIds[0],
          }
        })
        .filter(({ visit_id: vid, visit_schedule_id: vsid }) => vid && vsid)
    }

    if (visit_id && visit_schedule_id) {
      return [
        {
          visit_id,
          visit_schedule_id,
          study_schedule_id:
            getArmIdForVisitScheduleId(visit_schedule_id) || study_schedule_id || selectedArmIds[0],
        },
      ]
    }

    return []
  }, [
    storedVisitIds,
    getStoredVisitScheduleId,
    visit_id,
    visit_schedule_id,
    study_schedule_id,
    selectedArmIds,
    visitTemplates,
    cycles,
    getArmIdForVisitScheduleId,
  ])

  const armName =
    checkedArms.length === 1
      ? checkedArms[0].text
      : studyVisitSchedule?.find(item => item.id === study_schedule_id)?.name || ''

  const visitTemplatesList = useMemo(() => {
    const templates = visitTemplates || []
    const sortedTemplates = templates.map(({ id: tid, name, visits }) => ({
      field: tid,
      label: isArmsSupported && armName ? armName : name,
      hideGroupControl: true,
      value: sortVisitsByDay(visits).map(({ id, name: visitName }) => ({
        key: id,
        text: visitName,
        value: id,
      })),
    }))

    return sortedTemplates.sort((a, b) => {
      const aDay = templates.find(template => template.id === a.field)?.visits?.[0]
      const bDay = templates.find(template => template.id === b.field)?.visits?.[0]
      return getVisitDay(aDay) - getVisitDay(bDay)
    })
  }, [visitTemplates, isArmsSupported, armName])

  const hasVisitsInTemplates = visitTemplatesList.some(visitTemplate => {
    return !!visitTemplate.value.length
  })

  const formattedCyclesList = useMemo(() => {
    const armOrder = checkedArms.length > 0 ? checkedArms.map(arm => arm.key) : selectedArmIds
    const armIndex = armId => {
      const index = armOrder.indexOf(armId)
      return index === -1 ? Number.MAX_SAFE_INTEGER : index
    }

    const formatted = cycles?.map(cycle => {
      const sortedVisits = sortVisitsByDay(cycle?.visits)
      const formattedVisits = sortedVisits.map(visit => ({
        key: visit.id,
        text: visit.name,
        value: visit.id,
      }))
      const isDummy = isNonCycledCycle(cycle)

      return {
        field: cycle.id,
        label: isArmsSupported && cycle.armName
          ? (isDummy ? cycle.armName : `${cycle.armName}: ${cycle.name}`)
          : (isDummy ? '' : cycle.name),
        hideGroupControl: true,
        value: formattedVisits,
        armId: cycle.armId,
        sortDay: getVisitDay(sortedVisits[0]),
      }
    })

    const sorted = formatted
      ?.filter(cycle => cycle.value.length > 0)
      ?.sort((a, b) => {
        const byArm = armIndex(a.armId) - armIndex(b.armId)
        if (byArm !== 0) return byArm
        return (a.sortDay ?? 0) - (b.sortDay ?? 0)
      })

    let previousLabel = null
    return sorted?.map(({ sortDay, armId, ...cycle }) => {
      const isDuplicateArmLabel =
        !!cycle.label && cycle.label === previousLabel && !String(cycle.label).includes(':')
      previousLabel = cycle.label
      return isDuplicateArmLabel ? { ...cycle, label: '' } : cycle
    })
  }, [cycles, isArmsSupported, checkedArms, selectedArmIds])

  const hasVisitsInCycles = formattedCyclesList?.some(visitTemplate => {
    return !!visitTemplate?.value?.length
  })

  const hasInvalidUploadedVisit = useMemo(() => {
    if (!isArmsSupported || !Array.isArray(studyVisitSchedule) || studyVisitSchedule.length === 0) return false
    if (selectedArmIds.length === 0) return false

    return selectedArmIds.some(
      armId => !studyVisitSchedule.find(arm => arm.id === armId),
    )
  }, [isArmsSupported, studyVisitSchedule, selectedArmIds])

  const activeArmIds = useMemo(
    () => (checkedArms.length > 0 ? checkedArms.map(arm => arm.key) : selectedArmIds),
    [checkedArms, selectedArmIds],
  )

  const currentDeployVisits = useMemo(() => {
    const visitsFromChecked = []
    if (checkedVisitTemplate && typeof checkedVisitTemplate === 'object') {
      Object.keys(checkedVisitTemplate).forEach(visitScheduleId => {
        const scheduleGroup = checkedVisitTemplate[visitScheduleId]
        if (scheduleGroup && typeof scheduleGroup === 'object') {
          Object.keys(scheduleGroup).forEach(visitId => {
            if (scheduleGroup[visitId]) {
              visitsFromChecked.push({
                visit_id: visitId,
                visit_schedule_id: visitScheduleId,
                study_schedule_id: getArmIdForVisitScheduleId(visitScheduleId) || activeArmIds[0],
              })
            }
          })
        }
      })
    }

    if (visitsFromChecked.length > 0) return visitsFromChecked

    return deployVisits.map(visit => ({
      ...visit,
      study_schedule_id:
        visit.study_schedule_id ||
        getArmIdForVisitScheduleId(visit.visit_schedule_id) ||
        activeArmIds[0],
    }))
  }, [checkedVisitTemplate, deployVisits, getArmIdForVisitScheduleId, activeArmIds])

  const missingArmVisits = useMemo(
    () =>
      isArmsSupported && activeArmIds.length > 1
        ? getArmsMissingExpireVisits(activeArmIds, currentDeployVisits, visit =>
          getArmIdForVisitScheduleId(visit.visit_schedule_id) || visit.study_schedule_id,
        )
        : [],
    [isArmsSupported, activeArmIds, currentDeployVisits, getArmIdForVisitScheduleId],
  )

  const hasSelectedVisits = useMemo(() => {
    if (currentDeployVisits.length > 0) return true

    if (!checkedVisitTemplate || typeof checkedVisitTemplate !== 'object') return false
    return Object.keys(checkedVisitTemplate).some(scheduleId => {
      const scheduleGroup = checkedVisitTemplate[scheduleId]
      return scheduleGroup && typeof scheduleGroup === 'object' && Object.values(scheduleGroup).some(Boolean)
    })
  }, [currentDeployVisits.length, checkedVisitTemplate])

  const getSelectedVisitNames = useCallback(() => {
    const visitsFromChecked = []
    if (checkedVisitTemplate && typeof checkedVisitTemplate === 'object') {
      Object.keys(checkedVisitTemplate).forEach(visitScheduleId => {
        const scheduleGroup = checkedVisitTemplate[visitScheduleId]
        if (scheduleGroup && typeof scheduleGroup === 'object') {
          Object.keys(scheduleGroup).forEach(visitId => {
            if (scheduleGroup[visitId]) {
              visitsFromChecked.push({ visit_id: visitId, visit_schedule_id: visitScheduleId })
            }
          })
        }
      })
    }

    const visitsToDisplay = visitsFromChecked.length > 0 ? visitsFromChecked : deployVisits
    if (visitsToDisplay.length === 0) return []

    return visitsToDisplay
      .map(({ visit_id: visitId, visit_schedule_id: visitScheduleId }) => {
        if (isVisitCycleSupported) {
          const cycle = cycles?.find(item => item.id === visitScheduleId)
          const cycleVisit = cycle?.visits?.find(item => item.id === visitId)
          if (cycle && cycleVisit) {
            const isDummy = isNonCycledCycle(cycle)
            let tagText = cycleVisit.name
            if (isArmsSupported && cycle.armName) {
              tagText = isDummy
                ? `${cycle.armName}: ${cycleVisit.name}`
                : `${cycle.armName}: ${cycle.name}: ${cycleVisit.name}`
            } else if (!isDummy && cycle.name) {
              tagText = `${cycle.name}: ${cycleVisit.name}`
            }
            return {
              id: `${visitScheduleId}.${visitId}`,
              key: `${visitScheduleId}.${visitId}`,
              text: tagText,
              visit_id: visitId,
              visit_schedule_id: visitScheduleId,
            }
          }
        } else {
          const { _visitScheduleName, _visitName } = getScheduleAndVisitName({
            visit_id: visitId,
            visit_schedule_id: visitScheduleId,
            visitTemplates,
          })
          if (_visitName) {
            const tagText = isArmsSupported && armName
              ? `${armName}: ${_visitName}`
              : `${_visitScheduleName}: ${_visitName}`
            return {
              id: `${visitScheduleId}.${visitId}`,
              key: `${visitScheduleId}.${visitId}`,
              text: tagText,
              visit_id: visitId,
              visit_schedule_id: visitScheduleId,
            }
          }
        }
        return null
      })
      .filter(Boolean)
  }, [
    checkedVisitTemplate,
    deployVisits,
    isVisitCycleSupported,
    cycles,
    isArmsSupported,
    armName,
    visitTemplates,
  ])

  const selectedVisitTags = useMemo(() => getSelectedVisitNames(), [getSelectedVisitNames])

  const visitsAssignedLabel = useMemo(() => {
    const count = selectedVisitTags.length
    if (count === 0) return ''
    return count === 1 ? '1 visit assigned' : `${count} visits assigned`
  }, [selectedVisitTags.length])

  useEffect(() => {
    const visitsUnavailable = isVisitCycleSupported ? !hasVisitsInCycles : !hasVisitsInTemplates
    const requiresVisitSelection = !isArmsSupported || activeArmIds.length > 0
    const hasMissingArmVisits = isArmsSupported && activeArmIds.length > 1 && missingArmVisits.length > 0

    if (visitsUnavailable || (requiresVisitSelection && (!hasSelectedVisits || hasMissingArmVisits))) {
      const message = visitsUnavailable
        ? STRINGS.errPleaseASelectDiffDeployNoVisits
        : hasMissingArmVisits
          ? STRINGS.pleaseSelVisitFromEachArm
          : STRINGS.pleaseSelDifferentVisit
      updatePrevalidatedErrors({ [DEPLOY_VISIT_ERROR_KEY]: message })
    } else {
      clearPrevalidatedError(DEPLOY_VISIT_ERROR_KEY)
    }
  }, [
    activeArmIds,
    hasSelectedVisits,
    hasVisitsInCycles,
    hasVisitsInTemplates,
    isArmsSupported,
    isVisitCycleSupported,
    missingArmVisits.length,
    clearPrevalidatedError,
    updatePrevalidatedErrors,
  ])

  useEffect(() => {
    if (Array.isArray(visitTemplates) && visitTemplates.length > 0) return undefined
    let visitsNotFetched = true
    setVisitTemplatesLoading(true)
    fetchVisitTemplates(studyID).finally(() => {
      if (visitsNotFetched) setVisitTemplatesLoading(false)
    })
    return () => {
      visitsNotFetched = false
      clearPrevalidatedError(DEPLOY_VISIT_ERROR_KEY)
    }
  }, [])

  const invalidVisitWarning = hasInvalidUploadedVisit
    ? 'The arm/visit from the uploaded instrument does not exist in this study. Please select a valid arm and visit.'
    : ''

  useEffect(() => {
    const hydratedArms = selectedArmIds
      .map(armId => availableArms?.find(item => item.key === armId))
      .filter(Boolean)

    setCheckedArms(prev => {
      const hydratedIds = new Set(hydratedArms.map(arm => arm.key))
      const pendingArms = prev.filter(arm => !hydratedIds.has(arm.key))

      let next = prev
      if (hydratedArms.length > 0) {
        next = [...hydratedArms, ...pendingArms]
      } else if (prev.length > 0) {
        next = prev
      } else if (selectedArmIds.length === 0 && availableArms?.length === 1) {
        next = availableArms
      }

      if (
        next.length === prev.length &&
        next.every((arm, index) => arm.key === prev[index]?.key)
      ) {
        return prev
      }
      return next
    })
  }, [availableArms, selectedArmIds])

  useEffect(() => {
    const checked = deployVisits.reduce((acc, { visit_id: visitId, visit_schedule_id: visitScheduleId }) => {
      return setObjectProperty(acc, `${visitScheduleId}.${visitId}`, true)
    }, {})

    const deployVisitsIsEmpty = deployVisits.length === 0
    const hasCheckedItems = checkedVisitTemplate && Object.keys(checkedVisitTemplate).length > 0
    if (deployVisitsIsEmpty && hasCheckedItems) {
      return
    }

    const currentCheckedStr = JSON.stringify(checkedVisitTemplate)
    const newCheckedStr = JSON.stringify(checked)
    if (currentCheckedStr !== newCheckedStr) {
      setCheckedVisitTemplate(checked)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deployVisits])

  const buildDeploymentPayload = (
    updatedVisits,
    uiArmIds = checkedArms.length > 0 ? checkedArms.map(arm => arm.key) : selectedArmIds,
  ) => {
    const visitIds = updatedVisits.map(({ visit_id: visitId }) => visitId)
    // Keep visit_schedule_ids index-aligned with visit_ids for reliable edit hydration
    const visitScheduleIds = updatedVisits.map(
      ({ visit_schedule_id: visitScheduleId }) => visitScheduleId,
    )
    // Keep the arms the user just selected even before visits are chosen.
    // Filtering to visit-backed arms left Redux empty and made expire-sync
    // rewrite chain_expiry_info on every arm click (max update depth).
    const armIds =
      uiArmIds.length > 0
        ? uiArmIds
        : getPersistedStudyScheduleIds(uiArmIds, updatedVisits, visit =>
            getArmIdForVisitScheduleId(visit.visit_schedule_id),
          )

    const payload = {
      ...chain_deployment_info,
      [CHAIN_DEPLOY_BY_SS_VISIT]: true,
      [DEPLOYMENT_KEYS.visitIds]: visitIds,
      [DEPLOYMENT_KEYS.visitScheduleIds]: visitScheduleIds,
    }

    if (armIds.length > 0) {
      payload[DEPLOYMENT_KEYS.studyScheduleIds] = armIds
    } else {
      delete payload[DEPLOYMENT_KEYS.studyScheduleIds]
    }

    if (armIds.length === 1) {
      payload[DEPLOYMENT_KEYS.studyScheduleId] = armIds[0]
    } else {
      delete payload[DEPLOYMENT_KEYS.studyScheduleId]
    }

    if (updatedVisits.length === 1) {
      payload[DEPLOYMENT_KEYS.visitId] = updatedVisits[0].visit_id
      payload[DEPLOYMENT_KEYS.visitScheduleId] = updatedVisits[0].visit_schedule_id
    } else {
      // Keep legacy singular fields unset in multi-visit mode so recursive
      // schedule validation does not treat empty strings as missing required values.
      delete payload[DEPLOYMENT_KEYS.visitId]
      delete payload[DEPLOYMENT_KEYS.visitScheduleId]
    }

    return payload
  }

  const getCurrentVisits = () => {
    const currentVisitsFromChecked = []
    if (checkedVisitTemplate && typeof checkedVisitTemplate === 'object') {
      Object.keys(checkedVisitTemplate).forEach(visitScheduleId => {
        const scheduleGroup = checkedVisitTemplate[visitScheduleId]
        if (scheduleGroup && typeof scheduleGroup === 'object') {
          Object.keys(scheduleGroup).forEach(visitId => {
            if (scheduleGroup[visitId]) {
              currentVisitsFromChecked.push({
                visit_id: visitId,
                visit_schedule_id: visitScheduleId,
                study_schedule_id:
                  getArmIdForVisitScheduleId(visitScheduleId) || selectedArmIds[0],
              })
            }
          })
        }
      })
    }

    const allCurrentVisits = deployVisits.map(visit => ({
      ...visit,
      study_schedule_id:
        visit.study_schedule_id ||
        getArmIdForVisitScheduleId(visit.visit_schedule_id) ||
        selectedArmIds[0],
    }))

    currentVisitsFromChecked.forEach(visit => {
      const exists = allCurrentVisits.some(
        ({ visit_id: vid, visit_schedule_id: vsid }) =>
          vid === visit.visit_id && vsid === visit.visit_schedule_id,
      )
      if (!exists) {
        allCurrentVisits.push(visit)
      }
    })

    return allCurrentVisits
  }

  const syncExpiryWithVisits = (remainingVisits, uiArmIds) => {
    if (!chain_expiry_info) return

    const remainingArmIds = getPersistedStudyScheduleIds(uiArmIds, remainingVisits, visit =>
      getArmIdForVisitScheduleId(visit.visit_schedule_id),
    )
    const nextExpiry = alignExpiryWithDeployment({
      chainExpiryInfo: chain_expiry_info,
      deployArmIds: remainingArmIds,
      deployVisitPairs: remainingVisits,
      resolveArmId: visit =>
        getArmIdForVisitScheduleId(visit.visit_schedule_id) || visit.study_schedule_id,
    })

    if (nextExpiry) {
      updateScheduleKey('chain_expiry_info', nextExpiry)
    }
  }

  const updateArmSelection = newCheckedArms => {
    const armIds = newCheckedArms.map(arm => arm.key)
    const updatedVisits = getCurrentVisits().filter(visit => {
      const visitArmId =
        getArmIdForVisitScheduleId(visit.visit_schedule_id) || visit.study_schedule_id
      if (!visitArmId) return true
      return armIds.includes(visitArmId)
    })

    const newChecked = updatedVisits.reduce((acc, { visit_id: visitId, visit_schedule_id: visitScheduleId }) => {
      return setObjectProperty(acc, `${visitScheduleId}.${visitId}`, true)
    }, {})

    setCheckedArms(newCheckedArms)
    setCheckedVisitTemplate(newChecked)
    updateChainDeployInfo(buildDeploymentPayload(updatedVisits, armIds))
    syncExpiryWithVisits(updatedVisits, armIds)
  }

  const onToggleArm = itemOrKey => {
    const key = typeof itemOrKey === 'object' ? itemOrKey.key : itemOrKey
    const isSelected = checkedArms.some(arm => arm.key === key)
    const newCheckedArms = isSelected
      ? checkedArms.filter(arm => arm.key !== key)
      : [...checkedArms, availableArms?.find(item => item.key === key)].filter(Boolean)

    updateArmSelection(newCheckedArms)
  }

  const clearToggle = () => {
    setCheckedVisitTemplate({})
    updateChainDeployInfo(buildDeploymentPayload([]))
  }

  const onToggleVisit = item => {
    if (!item || item.disabled) return

    const isCurrentlyChecked = !!checkedVisitTemplate?.[item.field]?.[item.key]
    const allCurrentVisits = getCurrentVisits()

    let updatedVisits
    if (isCurrentlyChecked) {
      updatedVisits = allCurrentVisits.filter(
        ({ visit_id: visitId, visit_schedule_id: visitScheduleId }) =>
          !(visitId === item.key && visitScheduleId === item.field),
      )
    } else {
      const exists = allCurrentVisits.some(
        ({ visit_id: visitId, visit_schedule_id: visitScheduleId }) =>
          visitId === item.key && visitScheduleId === item.field,
      )
      updatedVisits = exists
        ? allCurrentVisits
        : [
          ...allCurrentVisits,
          {
            visit_id: item.key,
            visit_schedule_id: item.field,
            study_schedule_id: getArmIdForVisitScheduleId(item.field) || selectedArmIds[0],
          },
        ]
    }

    const newChecked = updatedVisits.reduce((acc, { visit_id: visitId, visit_schedule_id: visitScheduleId }) => {
      return setObjectProperty(acc, `${visitScheduleId}.${visitId}`, true)
    }, {})
    setCheckedVisitTemplate(newChecked)
    updateChainDeployInfo(buildDeploymentPayload(updatedVisits))
    syncExpiryWithVisits(updatedVisits)
  }

  const onRemoveVisitTag = tag => {
    const { visit_id: visitId, visit_schedule_id: visitScheduleId } = tag
    const updatedVisits = getCurrentVisits().filter(
      ({ visit_id: vid, visit_schedule_id: vsid }) => !(vid === visitId && vsid === visitScheduleId),
    )

    const newChecked = updatedVisits.reduce((acc, { visit_id: vid, visit_schedule_id: vsid }) => {
      return setObjectProperty(acc, `${vsid}.${vid}`, true)
    }, {})
    setCheckedVisitTemplate(newChecked)
    updateChainDeployInfo(buildDeploymentPayload(updatedVisits))
    syncExpiryWithVisits(updatedVisits)
  }

  const getErrorText = () => {
    if (isArmsSupported && activeArmIds.length === 0) {
      return 'Please select an arm first'
    }

    if (isVisitCycleSupported) {
      if (!hasVisitsInCycles) {
        return STRINGS.errPleaseASelectDiffDeployNoVisits
      }
      if (!hasSelectedVisits) {
        return STRINGS.pleaseSelDifferentVisit
      }
      if (missingArmVisits.length > 0) {
        return STRINGS.pleaseSelVisitFromEachArm
      }
      return errors['schedule.chain_deployment_info.visit_ids'] || errors['schedule.chain_deployment_info.visit_id']
    }

    if (!hasVisitsInTemplates) {
      return STRINGS.errPleaseASelectDiffDeployNoVisits
    }
    if (!hasSelectedVisits) {
      return STRINGS.pleaseSelDifferentVisit
    }
    if (missingArmVisits.length > 0) {
      return STRINGS.pleaseSelVisitFromEachArm
    }
    return errors['schedule.chain_deployment_info.visit_ids'] || errors['schedule.chain_deployment_info.visit_id']
  }

  const errorText = errors ? getErrorText() : ''
  const hasVisitFieldError = !!(
    errors?.['schedule.chain_deployment_info.visit_ids'] ||
    errors?.['schedule.chain_deployment_info.visit_id'] ||
    errors?.['schedule.chain_deployment_info.study_schedule_id']
  )

  const hasError =
    !!errorText ||
    hasVisitFieldError ||
    missingArmVisits.length > 0 ||
    (!isVisitCycleSupported && !hasVisitsInTemplates) ||
    (isVisitCycleSupported && !hasVisitsInCycles)

  const getList = () => (isVisitCycleSupported ? formattedCyclesList : visitTemplatesList)

  const checkIsDisabled = () => {
    if (isArmsSupported && checkedArms.length === 0) {
      return true
    }
    if (isVisitCycleSupported) {
      return isSelectionDisabled || !hasVisitsInCycles
    }
    return isSelectionDisabled || !hasVisitsInTemplates
  }

  const armSelectionCountLabel =
    checkedArms.length > 0
      ? `${checkedArms.length} arm${checkedArms.length === 1 ? '' : 's'} selected`
      : ''

  const armTagsProps = {
    tags: armsToTags(checkedArms),
    toggleItem: onToggleArm,
  }

  if (visitTemplatesLoading) return <Loader inContainer size={25} />
  return (
    <div className='arms-visits-selection'>
      {invalidVisitWarning && <WarningText text={invalidVisitWarning} />}
      {isArmsSupported ? (
        <div className='arms-visits-dropdown with-margin'>
          <div className='arm-name-label-container'>
            <div className='label-small'>Arm Name</div>
            {armSelectionCountLabel && (
              <div className='arm-selection-count'>
                <span className='arm-selection-count-number'>{checkedArms.length}</span>
                {` arm${checkedArms.length === 1 ? '' : 's'} selected`}
              </div>
            )}
          </div>
          <AutocompleteSearch
            loading={false}
            disabled={isSelectionDisabled}
            checked={armsToCheckedObject(checkedArms)}
            toggleItem={onToggleArm}
            shouldReturnItem
            deleteItemOnBackspace={!isSelectionDisabled}
            tagsProps={armTagsProps}
            showSelectedInList
            absolutePositionedList
            focusOnToggle
            id='instrument-arm'
            placeholder={ARM_SEARCH_PLACEHOLDER}
            list={availableArms}
          />
        </div>
      ) : null}

      <div className='arms-visits-dropdown'>
        <div className='visit-name-label-container'>
          <div className='label-small'>Visit Name</div>
          {visitsAssignedLabel ? (
            <div className='visits-assigned-label'>
              <span className='visits-assigned-count-number'>{selectedVisitTags.length}</span>
              {` visit${selectedVisitTags.length === 1 ? '' : 's'} assigned`}
            </div>
          ) : null}
        </div>
        {selectedVisitTags.length > 0 && (
          <div className='visit-tags-container'>
            {selectedVisitTags.map(tag => (
              <div key={tag.id} className='flexed center-aligned visit-tag'>
                <span className='visit-tag-text'>{tag.text}</span>
                {!isSelectionDisabled && (
                  <button
                    type='button'
                    className='visit-tag-remove'
                    onClick={() => onRemoveVisitTag(tag)}
                    aria-label={`Remove ${tag.text}`}>
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        <AutocompleteGroupSearch
          hasError={hasError}
          errorText={errorText}
          absolutePositionedList
          filterWithoutGroupSelected
          groupComponent='label'
          onBackspacePress={clearToggle}
          list={getList() || []}
          onSearchEmpty
          toggleItem={onToggleVisit}
          checked={checkedVisitTemplate}
          placeholder={STRINGS.searchForVisitPlaceholder}
          value=''
          disabled={checkIsDisabled()}
        />
      </div>
    </div>
  )
}

InstrumentVisitSelection.propTypes = {
  chain_deployment_info: PropTypes.shape({
    visit_id: PropTypes.string,
    visit_schedule_id: PropTypes.string,
    visit_ids: PropTypes.arrayOf(PropTypes.string),
    visit_schedule_ids: PropTypes.arrayOf(PropTypes.string),
    study_schedule_id: PropTypes.string,
  }),
  chain_expiry_info: PropTypes.shape({
    visit_ids: PropTypes.arrayOf(PropTypes.string),
    expiry_condition: PropTypes.shape({
      operator: PropTypes.string,
      terms: PropTypes.arrayOf(PropTypes.object),
    }),
  }),
  clearPrevalidatedError: PropTypes.func,
  disabled: PropTypes.bool,
  isReadOnlyState: PropTypes.bool,
  errors: PropTypes.objectOf(PropTypes.string),
  fetchVisitTemplates: PropTypes.func,
  isArmsSupported: PropTypes.bool,
  isVisitCycleSupported: PropTypes.bool,
  studyID: PropTypes.number,
  studyVisitSchedule: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    phases: PropTypes.arrayOf(PropTypes.shape({
      cycles: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        visits: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
      })),
      visit_templates: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
    })),
  })),
  updateChainDeployInfo: PropTypes.func,
  updatePrevalidatedErrors: PropTypes.func,
  updateScheduleKey: PropTypes.func,
  visitTemplates: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    visits: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  })),
}

export default InstrumentVisitSelection
